import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Shield, UserCog, KeyRound, Wrench, ToggleLeft, Crown, CreditCard, LayoutGrid, Copy, MessageCircle, Mail, Languages, BellRing, Download, Smartphone, Bell, Link2Off, Palette, Check, ChevronDown, GripVertical } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { BrutalButton, BrutalCard, SectionTitle, Pill } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { useAuth, isAdmin, isSuperAdmin } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { ROLE_LABELS, MODULE_TOGGLES } from "@/lib/labels";
import { Tip } from "@/components/Tip";
import { MemberNotifPrefs } from "@/components/MemberNotifPrefs";
import { useT } from "@/lib/i18n";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { GroupMultiSelect } from "@/components/Selects";
import { OrgIconContent } from "@/components/OrgIcon";
import { applyAccent, applyButtonShape, applyHeadingFont, ACCENT_PRESETS, DEFAULT_THEME, BTN_STYLES, FONTS } from "@/lib/theme";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import KioskLayoutEditor from "@/components/KioskLayoutEditor";

function Section({ title, icon: Icon, testid, defaultOpen = false, children }) {
  const [openS, setOpenS] = useState(defaultOpen);
  return (
    <div data-testid={testid}>
      <button data-testid={`${testid}-toggle`} onClick={() => setOpenS(!openS)}
        className="w-full flex items-center justify-between gap-2 brutal brutal-press rounded-2xl bg-white px-4 py-3.5 font-head font-black text-lg">
        <span className="flex items-center gap-2.5 min-w-0">{Icon && <Icon className="w-5 h-5 shrink-0" strokeWidth={2.5} />}<span className="truncate">{title}</span></span>
        <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-200 ${openS ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
      {openS && <div className="space-y-5 mt-3">{children}</div>}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const t = useT();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "gebruiker", pin: "", group_ids: [], phone: "", birthdate: "" });
  const [retention, setRetention] = useState(7);
  const [pollRetention, setPollRetention] = useState(30);
  const [shopDash, setShopDash] = useState(false);
  const [shopNoPin, setShopNoPin] = useState(false);
  const [announceWin, setAnnounceWin] = useState(15);
  const [pollAutoClose, setPollAutoClose] = useState(false);
  const [keepOpen, setKeepOpen] = useState(true);
  const [autoAssign, setAutoAssign] = useState(false);
  const [proactiveAssign, setProactiveAssign] = useState(false);
  const [proactiveLeadMin, setProactiveLeadMin] = useState(180);
  const [rolloverEnabled, setRolloverEnabled] = useState(true);
  const [rolloverTime, setRolloverTime] = useState("18:00");
  const [busySkip, setBusySkip] = useState(3);
  const [digestSms, setDigestSms] = useState(false);
  const [pindateEditRestricted, setPindateEditRestricted] = useState(false);
  const [birthdayGift, setBirthdayGift] = useState(true);
  const [requests, setRequests] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [approveGroups, setApproveGroups] = useState({});
  const [modVis, setModVis] = useState({});
  const [subs, setSubs] = useState([]);
  const [subCfg, setSubCfg] = useState(null);
  const [payments, setPayments] = useState(null);
  const [dataReady, setDataReady] = useState(false);
  const [lockPinSet, setLockPinSet] = useState(false);
  const [lockPinInput, setLockPinInput] = useState("");
  const [lockPinBusy, setLockPinBusy] = useState(false);
  const [kioskLang, setKioskLang] = useState("nl");
  const [kioskMore, setKioskMore] = useState(null);
  const [kioskWheel, setKioskWheel] = useState(true);
  const [kioskPhoneTile, setKioskPhoneTile] = useState(true);
  const [kioskAllowance, setKioskAllowance] = useState(true);
  const [calView, setCalView] = useState("week");
  const [kioskDelete, setKioskDelete] = useState(false);
  const [kioskNoFs, setKioskNoFs] = useState(false);
  const [kioskDeleteWho, setKioskDeleteWho] = useState("admins");
  const [kioskLayoutRaw, setKioskLayoutRaw] = useState(null);
  const [rem, setRem] = useState({ reminder_specific_lead_min: 30, reminder_advance_days: 1, reminder_overdue_enabled: true, reminder_overdue_max_days: 0, reminder_max_per_day: 3, reminder_nudge_enabled: true, reminder_nudge_lead_min: 90, daypart_deadlines: { ochtend: "12:00", middag: "18:00", avond: "22:00" }, reminder_daypart_lead_min: 120 });
  const [memberDevices, setMemberDevices] = useState({});
  const [iconEmoji, setIconEmoji] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [iconBusy, setIconBusy] = useState(false);
  const [accent, setAccent] = useState("");
  const [btnStyle, setBtnStyle] = useState("pill");
  const [headFont, setHeadFont] = useState("Outfit");
  const { refreshSettings, refreshPending, refreshOrgs, orgs, activeOrg } = useAuth();
  const activeOrgObj = orgs?.find((o) => o.org_id === activeOrg);
  const effOrg = activeOrg;
  const orgName = activeOrgObj?.name || t("je organisatie");
  const joinPath = `/aanmelden${effOrg ? `?org=${effOrg}` : ""}`;
  const joinOrgLink = `${window.location.origin}${joinPath}`;
  const joinOrgQr = `${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(joinPath)}`;

  const load = async () => {
    const [u, g, s, r] = await Promise.all([api.get("/users"), api.get("/groups"), api.get("/settings"), api.get("/join-requests")]);
    setUsers(u.data); setGroups(g.data); setRetention(s.data.completed_retention_days ?? 7); setPollRetention(s.data.closed_poll_retention_days ?? 30); setPollAutoClose(s.data.poll_auto_close === true); setShopDash(s.data.shopping_on_dashboard === true); setShopNoPin(s.data.shopping_kiosk_no_pin === true); setAnnounceWin(Number(s.data.shopping_announce_window) || 15);
    setKeepOpen(s.data.keep_open_past_due !== false); setAutoAssign(!!s.data.auto_assign_past_due); setProactiveAssign(!!s.data.proactive_assign); setProactiveLeadMin(Number(s.data.proactive_assign_lead_min) || 180); setRolloverEnabled(s.data.rollover_enabled !== false); setRolloverTime(s.data.rollover_time || "18:00"); setBusySkip(Number(s.data.auto_assign_busy_skip) || 3); setDigestSms(!!s.data.digest_sms); setPindateEditRestricted(!!s.data.pindate_edit_restricted); setBirthdayGift(s.data.birthday_gift_reminder !== false);
    setModVis(s.data.module_visibility || {});
    setKioskLang(s.data.kiosk_language || "nl");
    setKioskMore(Array.isArray(s.data.kiosk_more_modules) ? s.data.kiosk_more_modules : null);
    setKioskWheel(s.data.kiosk_show_wheel !== false); setKioskPhoneTile(s.data.kiosk_show_phone_tile !== false); setKioskAllowance(s.data.kiosk_show_allowance !== false); setCalView(s.data.default_calendar_view || "week");
    setKioskDelete(!!s.data.kiosk_delete_enabled); setKioskDeleteWho(s.data.kiosk_delete_who || "admins");
    setKioskNoFs(!!s.data.kiosk_no_fullscreen);
    setKioskLayoutRaw(s.data.kiosk_layout || null);
    setIconEmoji(s.data.icon_emoji || ""); setIconUrl(s.data.icon_url || "");
    setAccent(s.data.theme_accent || "");
    setBtnStyle(s.data.theme_button || "pill"); setHeadFont(s.data.theme_font || "Outfit");
    setRem({
      reminder_specific_lead_min: s.data.reminder_specific_lead_min ?? 30,
      reminder_advance_days: s.data.reminder_advance_days ?? 1,
      reminder_overdue_enabled: s.data.reminder_overdue_enabled !== false,
      reminder_overdue_max_days: s.data.reminder_overdue_max_days ?? 0,
      reminder_max_per_day: s.data.reminder_max_per_day ?? 3,
      reminder_nudge_enabled: s.data.reminder_nudge_enabled !== false,
      reminder_nudge_lead_min: s.data.reminder_nudge_lead_min ?? 90,
      daypart_deadlines: { ochtend: "12:00", middag: "18:00", avond: "22:00", ...(s.data.daypart_deadlines || {}) },
      reminder_daypart_lead_min: s.data.reminder_daypart_lead_min ?? 120,
    });
    setRequests(r.data);
    try { setLockPinSet((await api.get("/settings/kiosk-lock-pin")).data.is_set); } catch {}
    try {
      const md = (await api.get("/org/members-devices")).data;
      const map = {}; (md || []).forEach((m) => { map[m.user_id] = m; }); setMemberDevices(map);
    } catch {}
    setDataReady(true);
  };
  useEffect(() => { if (isAdmin(user)) load(); }, [user]);

  // preload alle thema-fonts zodat de font-kiezer elke optie in eigen stijl toont
  useEffect(() => {
    const id = "theme-font-preview";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${FONTS.map((f) => `family=${f.css}`).join("&")}&display=swap`;
    document.head.appendChild(link);
  }, []);

  if (!isAdmin(user)) return <div className="text-center py-20"><Shield className="w-10 h-10 mx-auto mb-3" /><p className="font-head font-bold">{t("Alleen voor beheerders")}</p></div>;

  const toggleModule = async (key) => {
    if (!dataReady) return;
    const next = { ...modVis, [key]: modVis[key] === false ? true : false };
    setModVis(next);
    await api.put("/settings", { module_visibility: next });
    await refreshSettings();
    toast.success(next[key] === false ? t("Module uitgezet") : t("Module aangezet"));
  };

  const KIOSK_MORE_KEYS = [
    { key: "klusjes", label: "Klusjes" }, { key: "ruilmarkt", label: "Ruilmarkt" },
    { key: "eten", label: "Eten" }, { key: "rooster", label: "Rooster" },
    { key: "pindates", label: "Pin Dates" }, { key: "activiteiten", label: "Activiteiten" },
    { key: "onderhoud", label: "Onderhoud" }, { key: "kennisbank", label: "Hoedan, Wattan" },
  ];
  const moreEnabled = (k) => kioskMore === null || kioskMore.includes(k);
  const toggleKioskMore = async (key) => {
    if (!dataReady) return;
    const base = kioskMore === null ? KIOSK_MORE_KEYS.map((m) => m.key) : kioskMore;
    const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    setKioskMore(next);
    await api.put("/settings", { kiosk_more_modules: next });
    await refreshSettings();
    toast.success(t("Kiosk-tegel bijgewerkt"));
  };
  const toggleKioskWidget = async (key, cur, setter) => {
    if (!dataReady) return;
    const next = !cur;
    setter(next);
    await api.put("/settings", { [key]: next });
    await refreshSettings();
    toast.success(t("Kiosk-instelling bijgewerkt"));
  };

  const saveLayout = async (layout) => {
    setKioskLayoutRaw(layout);
    try { await api.put("/settings", { kiosk_layout: layout }); await refreshSettings(); toast.success(t("Kiosk-indeling opgeslagen")); }
    catch { toast.error(t("Mislukt")); }
  };

  const saveLockPin = async (clear = false) => {
    const pinVal = clear ? "" : lockPinInput;
    if (!clear && !(/^\d{4,6}$/.test(pinVal))) { toast.error(t("Pincode moet 4 tot 6 cijfers zijn")); return; }
    setLockPinBusy(true);
    try {
      const { data } = await api.post("/settings/kiosk-lock-pin", { pin: pinVal });
      setLockPinSet(data.is_set); setLockPinInput("");
      toast.success(clear ? t("Vergrendel-PIN verwijderd") : t("Vergrendel-PIN opgeslagen"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setLockPinBusy(false); }
  };

  const saveSubCfg = async () => {};

  const approveReq = async (req) => {
    await api.post(`/join-requests/${req.id}/approve`, { group_ids: approveGroups[req.id] || [], base_url: window.location.origin });
    toast.success(`${req.name} ${t("goedgekeurd — uitnodiging verstuurd")}`); load(); refreshPending?.();
  };
  const rejectReq = async (req) => { await api.post(`/join-requests/${req.id}/reject`); toast.success(t("Afgewezen")); load(); refreshPending?.(); };

  const unlinkDevice = async (deviceId, name) => {
    if (!(await confirm({ title: t("Apparaat ontkoppelen?"), description: `${name} ${t("moet daarna opnieuw inloggen op dat apparaat.")}` }))) return;
    try { await api.delete(`/org/devices/${deviceId}`); toast.success(t("Apparaat ontkoppeld")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const saveRetention = async () => {
    await api.put("/settings", { completed_retention_days: Number(retention), closed_poll_retention_days: Number(pollRetention), keep_open_past_due: keepOpen, auto_assign_past_due: autoAssign, proactive_assign: proactiveAssign, proactive_assign_lead_min: Number(proactiveLeadMin) || 180, rollover_enabled: rolloverEnabled, rollover_time: rolloverTime || "18:00", auto_assign_busy_skip: Number(busySkip) || 3, digest_sms: digestSms, pindate_edit_restricted: pindateEditRestricted, birthday_gift_reminder: birthdayGift });
    toast.success(t("Instellingen opgeslagen"));
  };

  const saveReminders = async () => {
    const payload = {
      reminder_specific_lead_min: Math.max(0, Number(rem.reminder_specific_lead_min) || 0),
      reminder_advance_days: Math.max(0, Number(rem.reminder_advance_days) || 0),
      reminder_overdue_enabled: !!rem.reminder_overdue_enabled,
      reminder_overdue_max_days: Math.max(0, Number(rem.reminder_overdue_max_days) || 0),
      reminder_max_per_day: Math.max(1, Number(rem.reminder_max_per_day) || 1),
      reminder_nudge_enabled: !!rem.reminder_nudge_enabled,
      reminder_nudge_lead_min: Math.max(0, Number(rem.reminder_nudge_lead_min) || 0),
      daypart_deadlines: rem.daypart_deadlines,
      reminder_daypart_lead_min: Math.max(0, Number(rem.reminder_daypart_lead_min) || 0),
    };
    await api.put("/settings", payload);
    toast.success(t("Instellingen opgeslagen"));
  };

  const downloadReminderGuide = async () => {
    try {
      const { data } = await api.get("/tasks/reminders/guide", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error(t("Mislukt")); }
  };

  const HH_EMOJIS = ["🏠", "🏡", "🏘️", "👨‍👩‍👧", "👨‍👩‍👧‍👦", "👪", "🎓", "🏢", "🏥", "💼", "🐶", "🐱", "🌱", "🌻", "⭐", "❤️", "🎉", "🍽️", "🛋️", "🧹", "🏀", "🎮", "📚", "☕"];
  const saveIconEmoji = async (em) => {
    setIconBusy(true);
    try {
      await api.put("/settings", { icon_emoji: em, icon_url: "" });
      setIconEmoji(em); setIconUrl(""); await refreshSettings(); refreshOrgs?.();
      toast.success(t("Huishouden-icoon bijgewerkt"));
    } catch { toast.error(t("Mislukt")); } finally { setIconBusy(false); }
  };
  const uploadIcon = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error(t("Afbeelding mag max. 3 MB zijn")); e.target.value = ""; return; }
    setIconBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings", { icon_url: data.url, icon_emoji: "" });
      setIconUrl(data.url); setIconEmoji(""); await refreshSettings(); refreshOrgs?.();
      toast.success(t("Huishouden-icoon bijgewerkt"));
    } catch { toast.error(t("Uploaden mislukt")); } finally { setIconBusy(false); e.target.value = ""; }
  };
  const removeIcon = async () => {
    setIconBusy(true);
    try { await api.put("/settings", { icon_emoji: "", icon_url: "" }); setIconEmoji(""); setIconUrl(""); await refreshSettings(); refreshOrgs?.(); toast.success(t("Icoon verwijderd")); }
    catch { toast.error(t("Mislukt")); } finally { setIconBusy(false); }
  };

  const previewAccent = (hex) => { setAccent(hex); applyAccent(hex); };
  const saveAccent = async (hex) => {
    const val = (hex ?? accent) || "";
    try {
      await api.put("/settings", { theme_accent: val });
      applyAccent(val || null); setAccent(val); await refreshSettings(); refreshOrgs?.();
      toast.success(t("Accentkleur opgeslagen"));
    } catch { toast.error(t("Mislukt")); }
  };
  const resetAccent = async () => {
    try { await api.put("/settings", { theme_accent: "" }); setAccent(""); applyAccent(null); await refreshSettings(); refreshOrgs?.(); toast.success(t("Standaardkleur hersteld")); }
    catch { toast.error(t("Mislukt")); }
  };
  const saveBtnStyle = async (id) => {
    setBtnStyle(id); applyButtonShape(id);
    try { await api.put("/settings", { theme_button: id }); await refreshSettings(); toast.success(t("Knopvorm opgeslagen")); }
    catch { toast.error(t("Mislukt")); }
  };
  const saveHeadFont = async (id) => {
    setHeadFont(id); applyHeadingFont(id);
    try { await api.put("/settings", { theme_font: id }); await refreshSettings(); toast.success(t("Lettertype opgeslagen")); }
    catch { toast.error(t("Mislukt")); }
  };

  const create = async () => {
    if (!form.name) return toast.error(t("Naam verplicht"));
    try {
      await api.post("/users", { ...form, email: form.email || null, pin: form.pin || null, phone: form.phone || null, birthdate: form.birthdate || null });
      setOpen(false); setForm({ name: "", email: "", role: "gebruiker", pin: "", group_ids: [], phone: "", birthdate: "" }); load(); toast.success(t("Gebruiker aangemaakt"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const setRole = async (u, role) => { await api.put(`/users/${u.user_id}`, { role }); load(); };
  const toggleFacilitair = async (u) => {
    const cur = u.extra_roles || [];
    const next = cur.includes("facilitair") ? cur.filter((x) => x !== "facilitair") : [...cur, "facilitair"];
    await api.put(`/users/${u.user_id}`, { extra_roles: next }); load();
    toast.success(next.includes("facilitair") ? t("Facilitair aangezet") : t("Facilitair uitgezet"));
  };
  const toggleGroup = async (u, gid) => {
    const gids = u.group_ids?.includes(gid) ? u.group_ids.filter((x) => x !== gid) : [...(u.group_ids || []), gid];
    await api.put(`/users/${u.user_id}`, { group_ids: gids }); load();
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <button onClick={() => navigate("/meer")} className="flex items-center gap-1.5 font-bold text-sm text-brand-ink/60"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />{t("Meer")}</button>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0"><SectionTitle sub={t("Gebruikers, rollen & groepen")}>{t("Instellingen")}</SectionTitle></div>
        <Tip label={t("Nieuwe gebruiker")}><BrutalButton data-testid="new-user-btn" className="shrink-0" onClick={() => setOpen(true)}><Plus className="w-5 h-5" strokeWidth={3} /></BrutalButton></Tip>
      </div>

      <Section title={t("Weergave")} icon={Palette} testid="section-weergave">
        <BrutalCard data-testid="accent-color-card">
          <p className="font-head font-bold flex items-center gap-2"><Palette className="w-4 h-4" strokeWidth={2.5} />{t("Accentkleur")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("Kies de hoofdkleur van je huishouden. Knoppen en accenten kleuren direct mee.")}</p>
          <div className="rounded-2xl brutal-sm bg-brand-cream p-4 mb-4" data-testid="theme-preview">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-full bg-accent-solid flex items-center justify-center overflow-hidden text-lg shrink-0"><OrgIconContent emoji={iconEmoji} url={iconUrl} type={activeOrgObj?.type} /></span>
              <h3 className="font-head font-black text-xl leading-none">{orgName}</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <BrutalButton size="sm">{t("Voorbeeldknop")}</BrutalButton>
              <span className="bg-accent-solid brutal-sm rounded-full px-2.5 py-1 text-xs font-bold font-head">{t("Accent")}</span>
              <span className="bg-white brutal-sm rounded-full px-2.5 py-1 text-xs font-bold font-head">{t("Vandaag")}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {ACCENT_PRESETS.map((p) => {
              const active = (accent || "").toLowerCase() === p.hex.toLowerCase();
              return (
                <button key={p.hex} data-testid={`accent-preset-${p.hex}`} title={p.name} onClick={() => saveAccent(p.hex)}
                  className="w-9 h-9 rounded-full brutal-sm brutal-press flex items-center justify-center" style={{ background: p.hex }}>
                  {active && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <label className="flex items-center gap-2 font-bold text-sm cursor-pointer">
              <input type="color" data-testid="accent-color-input" value={accent || DEFAULT_THEME.accent}
                onChange={(e) => previewAccent(e.target.value)} onBlur={(e) => saveAccent(e.target.value)}
                className="w-10 h-10 rounded-lg border-2 border-brand-ink/20 cursor-pointer bg-white p-0.5" />
              {t("Eigen kleur")}
            </label>
            <BrutalButton size="sm" data-testid="accent-preview-btn">{t("Voorbeeld")}</BrutalButton>
            {accent && <BrutalButton size="sm" variant="white" data-testid="accent-reset-btn" onClick={resetAccent}>{t("Standaard")}</BrutalButton>}
          </div>

          <div className="mt-4 pt-3 border-t-2 border-brand-ink/10">
            <p className="font-bold text-sm mb-1.5">{t("Knopvorm")}</p>
            <div className="flex gap-2" data-testid="btn-shape-group">
              {BTN_STYLES.map((b) => (
                <button key={b.id} data-testid={`btn-shape-${b.id}`} onClick={() => saveBtnStyle(b.id)}
                  className={`flex-1 brutal-sm brutal-press py-2 font-bold text-sm ${btnStyle === b.id ? "bg-accent-solid" : "bg-white"}`}
                  style={{ borderRadius: b.id === "pill" ? "9999px" : b.id === "soft" ? "0.9rem" : "0.35rem" }}>
                  {t(b.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t-2 border-brand-ink/10">
            <p className="font-bold text-sm mb-1.5">{t("Lettertype koppen")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="head-font-group">
              {FONTS.map((f) => (
                <button key={f.id} data-testid={`head-font-${f.id}`} onClick={() => saveHeadFont(f.id)}
                  className={`brutal-sm brutal-press rounded-xl py-2 px-2 text-lg font-black ${headFont === f.id ? "bg-accent-solid" : "bg-white"}`}
                  style={{ fontFamily: `'${f.id}', sans-serif` }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </BrutalCard>

        <BrutalCard data-testid="household-icon-card">
          <p className="font-head font-bold flex items-center gap-2"><LayoutGrid className="w-4 h-4" strokeWidth={2.5} />{t("Huishouden-icoon")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("Kies een emoji of upload een eigen afbeelding. Zichtbaar in de wisselaar, op het kioskbord en bij uitnodigingen.")}</p>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-16 h-16 rounded-2xl brutal-sm bg-white flex items-center justify-center text-3xl overflow-hidden shrink-0" data-testid="household-icon-preview">
              <OrgIconContent emoji={iconEmoji} url={iconUrl} type={activeOrgObj?.type} />
            </div>
            <div className="flex flex-col gap-2">
              <label className={`brutal-sm brutal-press rounded-full px-3 py-2 bg-brand-yellow font-bold text-sm flex items-center gap-2 cursor-pointer ${iconBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="household-icon-upload-label">
                <Download className="w-4 h-4 rotate-180" strokeWidth={2.5} />{t("Afbeelding uploaden")}
                <input type="file" accept="image/*" className="hidden" onChange={uploadIcon} data-testid="household-icon-upload-input" />
              </label>
              {(iconEmoji || iconUrl) && (
                <button data-testid="household-icon-remove" onClick={removeIcon} disabled={iconBusy}
                  className="brutal-sm brutal-press rounded-full px-3 py-1.5 bg-white font-bold text-sm text-brand-pink flex items-center gap-2">
                  <Trash2 className="w-4 h-4" strokeWidth={2.5} />{t("Icoon verwijderen")}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-8 gap-1.5">
            {HH_EMOJIS.map((em) => (
              <button key={em} data-testid={`household-icon-emoji-${em}`} onClick={() => saveIconEmoji(em)} disabled={iconBusy}
                className={`aspect-square rounded-xl flex items-center justify-center text-2xl brutal-press ${iconEmoji === em ? "brutal bg-brand-green" : "brutal-sm bg-white"}`}>
                {em}
              </button>
            ))}
          </div>
        </BrutalCard>
      </Section>

      <Section title={t("Uitnodigen & Gebruikers")} icon={UserCog} testid="section-gebruikers">
        <BrutalCard data-testid="invite-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl brutal-sm bg-white flex items-center justify-center text-xl overflow-hidden shrink-0">
                <OrgIconContent emoji={iconEmoji} url={iconUrl} type={activeOrgObj?.type} />
              </div>
              <div className="min-w-0">
                <p className="font-head font-bold flex items-center gap-2"><UserCog className="w-4 h-4 shrink-0" strokeWidth={2.5} />{t("Medegebruikers uitnodigen")}
                  <InfoTip testid="info-invite" text={t("Nodig huisgenoten uit door ze de QR-code te laten scannen of een e-mail/link te sturen; de organisatie zit al aan de uitnodiging gekoppeld. Voorbeeld: je kind scant de QR met de telefoon en zit meteen in jullie huishouden — geen organisatie-ID intypen nodig.")} /></p>
                <p className="text-xs text-brand-ink/60 mt-0.5">{t("Laat iemand de QR scannen om zich aan te melden")}</p>
              </div>
            </div>
            <BrutalButton data-testid="show-qr-btn" size="sm" className="shrink-0" onClick={() => setQrOpen(true)}>{t("QR tonen")}</BrutalButton>
          </div>
          {requests.length > 0 && (
            <div className="mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15 space-y-3">
              <p className="font-bold text-sm">{t("Aanmeldingen")} ({requests.length})</p>
              {requests.map((r) => (
                <div key={r.id} data-testid={`join-req-${r.id}`} className="brutal-sm rounded-xl bg-brand-cream p-3">
                  <p className="font-head font-bold">{r.name}</p>
                  <p className="text-xs text-brand-ink/60 font-semibold">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                  {r.message && <p className="text-xs italic mt-0.5">"{r.message}"</p>}
                  <div className="mt-2">
                    <p className="text-xs font-bold mb-1">{t("Koppel aan groep(en):")}</p>
                    <GroupMultiSelect groups={groups} value={approveGroups[r.id] || []} onChange={(v) => setApproveGroups({ ...approveGroups, [r.id]: v })} testid={`join-groups-${r.id}`} />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <BrutalButton data-testid={`approve-${r.id}`} variant="green" size="sm" className="flex-1" onClick={() => approveReq(r)}>{t("Goedkeuren")}</BrutalButton>
                    <BrutalButton data-testid={`reject-${r.id}`} variant="white" size="sm" onClick={() => rejectReq(r)}>{t("Afwijzen")}</BrutalButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BrutalCard>

        <div className="space-y-3">
          {users.map((u) => (
            <BrutalCard key={u.user_id} data-testid={`user-${u.user_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 brutal-sm rounded-full bg-brand-cream flex items-center justify-center font-head font-black">{u.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <p className="font-head font-bold flex items-center gap-1.5">{u.name} {u.has_pin && <KeyRound className="w-3.5 h-3.5 text-brand-green" strokeWidth={3} />}</p>
                    <p className="text-xs text-brand-ink/50">{u.email || t("PIN-gebruiker")}</p>
                  </div>
                </div>
                {u.user_id !== user.user_id && (
                  <Tip label={t("Gebruiker verwijderen")}><button data-testid={`delete-user-${u.user_id}`} onClick={async () => { if (await confirm({ title: t("Gebruiker verwijderen?"), description: `${u.name} ${t("wordt verwijderd.")}` })) api.delete(`/users/${u.user_id}`).then(load); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button></Tip>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {["gebruiker", "beheerder", "super_admin"].map((r) => (
                  <button key={r} data-testid={`role-${u.user_id}-${r}`} onClick={() => setRole(u, r)}
                    className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold ${u.role === r ? "bg-brand-blue text-white" : "bg-white"}`}>{t(ROLE_LABELS[r])}</button>
                ))}
                <button data-testid={`facilitair-${u.user_id}`} onClick={() => toggleFacilitair(u)}
                  className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1 ${(u.extra_roles || []).includes("facilitair") ? "bg-brand-violet text-white" : "bg-white"}`}>
                  <Wrench className="w-3 h-3" strokeWidth={2.5} />{t("Facilitair")}</button>
              </div>
              {groups.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {groups.map((g) => (
                    <button key={g.id} onClick={() => toggleGroup(u, g.id)}
                      className={`brutal-sm brutal-press rounded-full px-2.5 py-0.5 text-xs font-bold ${u.group_ids?.includes(g.id) ? "text-white" : "bg-white opacity-60"}`}
                      style={u.group_ids?.includes(g.id) ? { backgroundColor: g.color } : {}}>{g.name}</button>
                  ))}
                </div>
              )}
              {(() => {
                const md = memberDevices[u.user_id];
                if (!md || (md.device_count === 0 && md.push_count === 0)) return null;
                const PLATFORM = { ios: "iOS", android: "Android", windows: "Windows", macos: "macOS", chromeos: "ChromeOS", linux: "Linux", web: "Web" };
                return (
                  <div className="mt-3 border-t-2 border-brand-ink/10 pt-2.5" data-testid={`user-devices-${u.user_id}`}>
                    <p className="text-xs font-bold text-brand-ink/60 flex items-center gap-1.5 mb-1.5">
                      <Smartphone className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Apparaten")}
                      {md.push_count > 0 && <span className="inline-flex items-center gap-1 text-brand-violet"><Bell className="w-3 h-3" strokeWidth={2.5} />{md.push_count}</span>}
                    </p>
                    {md.devices.length === 0
                      ? <p className="text-xs text-brand-ink/40">{t("Notificaties aan, geen geïnstalleerde app.")}</p>
                      : (
                        <div className="space-y-1.5">
                          {md.devices.map((d) => (
                            <div key={d.device_id} className="flex items-center gap-2 brutal-sm rounded-lg bg-brand-cream px-2.5 py-1.5 text-xs">
                              <Smartphone className="w-3.5 h-3.5 text-brand-ink/40 shrink-0" strokeWidth={2.5} />
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-brand-ink/80">{PLATFORM[d.platform] || d.platform || t("Onbekend")}{d.installed ? ` · ${t("geïnstalleerd")}` : ""}</p>
                                <p className="text-brand-ink/40 truncate">{t("Laatst actief")} {d.last_seen ? new Date(d.last_seen).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</p>
                              </div>
                              <button data-testid={`device-unlink-${d.device_id}`} onClick={() => unlinkDevice(d.device_id, u.name)}
                                className="inline-flex items-center gap-1 brutal-sm brutal-press rounded-full px-2 py-1 bg-white font-bold text-brand-pink shrink-0">
                                <Link2Off className="w-3 h-3" strokeWidth={2.5} />{t("Ontkoppel")}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                );
              })()}
            </BrutalCard>
          ))}
        </div>
      </Section>

      <Section title={t("Taken & Meldingen")} icon={BellRing} testid="section-meldingen">
        <BrutalCard data-testid="retention-card">
          <p className="font-head font-bold flex items-center gap-2"><Shield className="w-4 h-4" strokeWidth={2.5} />{t("Afgeronde taken bewaren")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">{t("Hoelang blijven afgeronde taken in het overzicht staan?")}</p>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="retention-input" type="number" min="1" value={retention} onChange={(e) => setRetention(e.target.value)}
              className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
            <span className="font-bold text-sm">{t("dagen")}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15">
            <span className="font-bold text-sm flex-1 min-w-0">{t("Gesloten polls bewaren")}</span>
            <input data-testid="poll-retention-input" type="number" min="1" value={pollRetention} onChange={(e) => setPollRetention(e.target.value)}
              className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
            <span className="font-bold text-sm">{t("dagen")}</span>
          </div>
          <button data-testid="toggle-poll-auto-close" onClick={async () => { const v = !pollAutoClose; setPollAutoClose(v); await api.put("/settings", { poll_auto_close: v }); await refreshSettings?.(); toast.success(t("Opgeslagen")); }}
            className="w-full mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15 flex items-center justify-between gap-3 font-bold text-sm text-left">
            <span className="min-w-0">{t("Poll automatisch sluiten zodra iedereen heeft gestemd")}
              <span className="block text-xs font-semibold text-brand-ink/50">{t("Uit = altijd wachten tot de sluitingstijd.")}</span>
            </span>
            <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${pollAutoClose ? "bg-brand-green" : "bg-white"}`}>{pollAutoClose && <span className="text-xs">✓</span>}</span>
          </button>
          <button data-testid="toggle-keep-open" onClick={() => setKeepOpen(!keepOpen)}
            className="w-full mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15 flex items-center justify-between font-bold text-sm">
            {t("Taak open houden na verstreken due-datum")}
            <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${keepOpen ? "bg-brand-green" : "bg-white"}`}>{keepOpen && <span className="text-xs">✓</span>}</span>
          </button>
          <button data-testid="toggle-auto-assign" onClick={() => setAutoAssign(!autoAssign)}
            className="w-full mt-2 flex items-center justify-between font-bold text-sm">
            <span>{t("Verlopen taken automatisch & eerlijk toewijzen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Slim verdeeld o.b.v. wie taken heeft uitgevoerd")}</span></span>
            <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${autoAssign ? "bg-brand-green" : "bg-white"}`}>{autoAssign && <span className="text-xs">✓</span>}</span>
          </button>
          <div className="mt-2 flex items-center gap-2">
            <button data-testid="toggle-proactive-assign" onClick={() => setProactiveAssign(!proactiveAssign)}
              className="flex-1 flex items-center justify-between font-bold text-sm">
              <span>{t("Vóór sluitingstijd automatisch toewijzen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Als niemand is toegewezen kort vóór de eindtijd — met push/SMS-melding")}</span></span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${proactiveAssign ? "bg-brand-green" : "bg-white"}`}>{proactiveAssign && <span className="text-xs">✓</span>}</span>
            </button>
            <InfoTip testid="info-proactive" text={t("Slackr wijst een openstaande taak automatisch aan iemand toe als er kort vóór de eindtijd nog niemand op staat. Voorbeeld: 'Afwas' moet om 20:00 klaar zijn en de voorsprong staat op 180 min → om 17:00 kiest Slackr een geschikt persoon en stuurt die een push/SMS. Zo blijft geen taak onopgemerkt liggen.")} />
          </div>
          {proactiveAssign && (
            <div className="mt-2 flex items-center gap-2 text-sm font-bold pl-1" data-testid="proactive-lead-row">
              <span>{t("Toewijzen")}</span>
              <input type="number" min="15" step="15" data-testid="proactive-lead-input" value={proactiveLeadMin}
                onChange={(e) => setProactiveLeadMin(e.target.value)}
                className="w-20 brutal-sm rounded-lg px-2 py-1 text-center outline-none bg-brand-cream" />
              <span className="text-brand-ink/60">{t("minuten vóór de sluitingstijd (bv. 180 = 3 uur)")}</span>
            </div>
          )}
          <div className="mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15">
            <button data-testid="toggle-rollover" onClick={() => setRolloverEnabled(!rolloverEnabled)}
              className="w-full flex items-center justify-between font-bold text-sm">
              <span>{t("Niet-opgepakte taken doorschuiven naar morgen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Na de afkaptijd verhuist een openstaande, niet-toegewezen taak naar de volgende dag — zonder strafpunten")}</span></span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${rolloverEnabled ? "bg-brand-green" : "bg-white"}`}>{rolloverEnabled && <span className="text-xs">✓</span>}</span>
            </button>
            {rolloverEnabled && (
              <div className="mt-2 flex items-center gap-2 text-sm font-bold pl-1" data-testid="rollover-time-row">
                <span>{t("Afkaptijd")}</span>
                <input type="time" data-testid="rollover-time-input" value={rolloverTime}
                  onChange={(e) => setRolloverTime(e.target.value)}
                  className="brutal-sm rounded-lg px-2 py-1 text-center outline-none bg-brand-cream" />
                <span className="text-brand-ink/60">{t("(bv. 18:00)")}</span>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 text-sm font-bold pl-1" data-testid="busy-skip-row">
              <span>{t("Bij auto-toewijzen overslaan wie vandaag al")}</span>
              <input type="number" min="1" step="1" data-testid="busy-skip-input" value={busySkip}
                onChange={(e) => setBusySkip(e.target.value)}
                className="w-16 brutal-sm rounded-lg px-2 py-1 text-center outline-none bg-brand-cream" />
              <span className="text-brand-ink/60">{t("taken deed")}</span>
            </div>
            <button data-testid="toggle-digest-sms" onClick={() => setDigestSms(!digestSms)}
              className="w-full mt-3 pt-3 border-t-2 border-dashed border-brand-ink/15 flex items-center justify-between font-bold text-sm">
              <span>{t("Dagelijkse ochtendsamenvatting ook via SMS")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Wie taken heeft, krijgt het dagoverzicht 's ochtends ook per SMS (naast push/e-mail)")}</span></span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${digestSms ? "bg-brand-green" : "bg-white"}`}>{digestSms && <span className="text-xs">✓</span>}</span>
            </button>
          </div>
          <button data-testid="toggle-birthday-gift" onClick={() => setBirthdayGift(!birthdayGift)}
            className="w-full mt-2 flex items-center justify-between font-bold text-sm">
            <span>{t("🎁 Cadeau-herinnering bij verjaardagen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Push 10 dagen vooraf (jarige krijgt niks — verrassing blijft)")}</span></span>
            <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${birthdayGift ? "bg-brand-green" : "bg-white"}`}>{birthdayGift && <span className="text-xs">✓</span>}</span>
          </button>
          <div className="mt-2 flex items-center gap-2">
            <button data-testid="toggle-pindate-edit-restricted" onClick={() => setPindateEditRestricted(!pindateEditRestricted)}
              className="flex-1 flex items-center justify-between font-bold text-sm">
              <span>{t("📌 Pin dates beschermen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Alleen een beheerder of de maker mag een pin date wijzigen/verwijderen op de kiosk")}</span></span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${pindateEditRestricted ? "bg-brand-green" : "bg-white"}`}>{pindateEditRestricted && <span className="text-xs">✓</span>}</span>
            </button>
            <InfoTip testid="info-pindate-protect" text={t("Standaard kan iedereen met een geldige kiosk-PIN een pin date wijzigen of verwijderen. Zet je dit aan, dan mag dat alleen nog een beheerder óf de persoon die de pin date zelf heeft aangemaakt. Voorbeeld: papa maakt 'Tandarts' aan; met deze optie aan kunnen de kinderen die niet meer per ongeluk verzetten of verwijderen.")} />
          </div>
          <BrutalButton data-testid="save-retention-btn" className="w-full mt-4" onClick={saveRetention}>{t("Opslaan")}</BrutalButton>
        </BrutalCard>

        <BrutalCard data-testid="reminders-card">
          <div className="flex items-center justify-between gap-2">
            <p className="font-head font-bold flex items-center gap-2"><BellRing className="w-4 h-4" strokeWidth={2.5} />{t("Taak-herinneringen")}</p>
            <button data-testid="reminders-pdf-btn" onClick={downloadReminderGuide} className="brutal-sm brutal-press rounded-full px-3 py-1.5 bg-white font-bold text-xs flex items-center gap-1.5"><Download className="w-3.5 h-3.5" strokeWidth={2.5} />{t("PDF-uitleg")}</button>
          </div>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("Bepaal wanneer en hoe vaak leden een herinnering krijgen voor taken met een due date.")}</p>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-bold text-sm flex-1 min-w-0">{t("Herinnering vóór specifiek tijdstip")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("minuten vooraf (0 = uit)")}</span></span>
            <div className="pt-3 border-t-2 border-dashed border-brand-ink/15">
              <span className="font-bold text-sm">{t("Dagdeel-eindtijden")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Tot hoe laat loopt een dagdeel? Herinneringen vertrekken vóór deze tijd.")}</span></span>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["ochtend", "middag", "avond"].map((dp) => (
                  <div key={dp}>
                    <label className="text-xs font-bold capitalize">{t(dp)}</label>
                    <input data-testid={`daypart-end-${dp}`} type="time" value={rem.daypart_deadlines?.[dp] || ""}
                      onChange={(e) => setRem({ ...rem, daypart_deadlines: { ...rem.daypart_deadlines, [dp]: e.target.value } })}
                      className="w-full brutal-sm rounded-xl px-2 py-2 font-medium outline-none text-center mt-1" />
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <span className="font-bold text-sm flex-1 min-w-0">{t("Herinnering vóór dagdeel-eindtijd")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("minuten vooraf (bv. 120 = 2 uur)")}</span></span>
                <input data-testid="daypart-lead-input" type="number" min="0" value={rem.reminder_daypart_lead_min} onChange={(e) => setRem({ ...rem, reminder_daypart_lead_min: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
              </div>
            </div>
              <input data-testid="rem-lead-input" type="number" min="0" value={rem.reminder_specific_lead_min} onChange={(e) => setRem({ ...rem, reminder_specific_lead_min: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t-2 border-dashed border-brand-ink/15">
              <span className="font-bold text-sm flex-1 min-w-0">{t("Vooraankondiging toekomstige taken")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("dagen vooraf (0 = uit)")}</span></span>
              <input data-testid="rem-advance-input" type="number" min="0" value={rem.reminder_advance_days} onChange={(e) => setRem({ ...rem, reminder_advance_days: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
            </div>
            <button data-testid="rem-overdue-toggle" onClick={() => setRem({ ...rem, reminder_overdue_enabled: !rem.reminder_overdue_enabled })}
              className="w-full pt-3 border-t-2 border-dashed border-brand-ink/15 flex items-center justify-between font-bold text-sm">
              {t("Verlopen taken opnieuw herinneren")}
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${rem.reminder_overdue_enabled ? "bg-brand-green" : "bg-white"}`}>{rem.reminder_overdue_enabled && <span className="text-xs">✓</span>}</span>
            </button>
            {rem.reminder_overdue_enabled && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-sm flex-1 min-w-0">{t("Stoppen na")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("dagen na de datum (0 = elke dag blijven herinneren)")}</span></span>
                <input data-testid="rem-overdue-days-input" type="number" min="0" value={rem.reminder_overdue_max_days} onChange={(e) => setRem({ ...rem, reminder_overdue_max_days: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
              </div>
            )}
            <button data-testid="rem-nudge-toggle" onClick={() => setRem({ ...rem, reminder_nudge_enabled: !rem.reminder_nudge_enabled })}
              className="w-full pt-3 border-t-2 border-dashed border-brand-ink/15 flex items-center justify-between font-bold text-sm text-left">
              <span className="flex-1 min-w-0">{t("Vriendelijke nudge vóór deadline")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("Herinnert nét voordat een taak te laat wordt")}</span></span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${rem.reminder_nudge_enabled ? "bg-brand-green" : "bg-white"}`}>{rem.reminder_nudge_enabled && <span className="text-xs">✓</span>}</span>
            </button>
            {rem.reminder_nudge_enabled && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-sm flex-1 min-w-0">{t("Nudge sturen")}<span className="block text-xs text-brand-ink/50 font-semibold">{t("minuten vóór de deadline")}</span></span>
                <input data-testid="rem-nudge-min-input" type="number" min="0" value={rem.reminder_nudge_lead_min} onChange={(e) => setRem({ ...rem, reminder_nudge_lead_min: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t-2 border-dashed border-brand-ink/15">
              <span className="font-bold text-sm flex-1 min-w-0">{t("Max. herinneringen per taak per dag")}</span>
              <input data-testid="rem-max-input" type="number" min="1" value={rem.reminder_max_per_day} onChange={(e) => setRem({ ...rem, reminder_max_per_day: e.target.value })} className="w-20 brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
            </div>
          </div>
          <BrutalButton data-testid="save-reminders-btn" className="w-full mt-4" onClick={saveReminders}>{t("Opslaan")}</BrutalButton>
        </BrutalCard>

        <MemberNotifPrefs />
      </Section>

      <Section title={t("Modules")} icon={ToggleLeft} testid="section-modules">
        <BrutalCard data-testid="modules-card">
          <p className="font-head font-bold flex items-center gap-2"><LayoutGrid className="w-4 h-4" strokeWidth={2.5} />{t("Modules aan/uit")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">{t("Zet onderdelen aan of uit. Uitgeschakelde modules verdwijnen overal (app + Kiosk).")}</p>
          <div className="space-y-1">
            {MODULE_TOGGLES.map((m) => {
              const on = modVis[m.key] !== false;
              return (
                <button key={m.key} data-testid={`module-toggle-${m.key}`} onClick={() => toggleModule(m.key)} disabled={!dataReady}
                  className="w-full flex items-center justify-between py-2 border-b-2 border-dashed border-brand-ink/10 last:border-0 disabled:opacity-50">
                  <span className="font-bold text-sm">{t(m.label)}</span>
                  <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${on ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                    <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
                  </span>
                </button>
              );
            })}
          </div>
          <button data-testid="toggle-shopping-dashboard" onClick={async () => { const v = !shopDash; setShopDash(v); await api.put("/settings", { shopping_on_dashboard: v }); await refreshSettings?.(); toast.success(t("Opgeslagen")); }} disabled={!dataReady}
            className="w-full flex items-center justify-between py-2 border-t-2 border-dashed border-brand-ink/10 mt-1 disabled:opacity-50">
            <span className="font-bold text-sm">🛒 {t("Boodschappenlijst-knop op Vandaag")}</span>
            <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${shopDash ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}><span className="w-5 h-5 rounded-full bg-white brutal-sm" /></span>
          </button>
          <div className="flex items-center justify-between py-2 border-t-2 border-dashed border-brand-ink/10 mt-1">
            <span className="font-bold text-sm">⏱️ {t("Aanvul-venster na belletje")}</span>
            <div className="flex gap-1" data-testid="announce-window">
              {[15, 30, 60].map((m) => (
                <button key={m} data-testid={`announce-window-${m}`} disabled={!dataReady} onClick={async () => { setAnnounceWin(m); await api.put("/settings", { shopping_announce_window: m }); await refreshSettings?.(); toast.success(t("Opgeslagen")); }}
                  className={`brutal-sm rounded-lg px-3 py-1.5 text-sm font-black disabled:opacity-50 ${announceWin === m ? "bg-brand-violet text-white" : "bg-white"}`}>{m}m</button>
              ))}
            </div>
          </div>
          <button data-testid="toggle-shopping-nopin" onClick={async () => { const v = !shopNoPin; setShopNoPin(v); await api.put("/settings", { shopping_kiosk_no_pin: v }); await refreshSettings?.(); toast.success(t("Opgeslagen")); }} disabled={!dataReady}
            className="w-full flex items-center justify-between py-2 border-t-2 border-dashed border-brand-ink/10 mt-1 disabled:opacity-50">
            <span className="font-bold text-sm text-left">🔓 {t("Boodschappen op kiosk zonder pincode bewerken")}</span>
            <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${shopNoPin ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}><span className="w-5 h-5 rounded-full bg-white brutal-sm" /></span>
          </button>
        </BrutalCard>
      </Section>

      <Section title={t("Kiosk")} icon={Smartphone} testid="section-kiosk">
        <BrutalCard data-testid="kiosk-more-card">
          <p className="font-head font-bold flex items-center gap-2"><LayoutGrid className="w-4 h-4" strokeWidth={2.5} />{t("Kiosk — 'Meer'-tegels")}
            <InfoTip testid="info-kiosk-more" text={t("Bepaalt welke extra onderdelen als alleen-lezen overzicht in het 'Meer'-blok op het kioskbord verschijnen (bv. recepten of mededelingen). Voorbeeld: zet 'Boodschappen' aan en iedereen ziet de lijst op de tablet; iets deelnemen of overnemen vraagt altijd nog om een PIN.")} /></p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">{t("Kies welke onderdelen als alleen-lezen overzicht in het 'Meer'-blok op de kiosk verschijnen. Deelnemen of overnemen vraagt altijd om een PIN.")}</p>
          <div className="space-y-1">
            {KIOSK_MORE_KEYS.filter((m) => modVis[m.key] !== false).map((m) => {
              const on = moreEnabled(m.key);
              return (
                <button key={m.key} data-testid={`kiosk-more-toggle-${m.key}`} onClick={() => toggleKioskMore(m.key)} disabled={!dataReady}
                  className="w-full flex items-center justify-between py-2 border-b-2 border-dashed border-brand-ink/10 last:border-0 disabled:opacity-50">
                  <span className="font-bold text-sm">{t(m.label)}</span>
                  <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${on ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                    <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
                  </span>
                </button>
              );
            })}
          </div>
        </BrutalCard>

        <BrutalCard data-testid="kiosk-widgets-card">
          <p className="font-head font-bold flex items-center gap-2"><LayoutGrid className="w-4 h-4" strokeWidth={2.5} />{t("Kiosk — widgets")}
            <InfoTip testid="info-kiosk-widgets" text={t("Zet losse blokken op het kioskbord aan of uit. Voorbeeld: heb je geen zakgeld-systeem, zet dan de 'Zakgeld-widget' uit — dan verdwijnt die van de tablet, terwijl de rest gewoon blijft staan.")} /></p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">{t("Verberg onderdelen die je niet op het kioskbord wilt tonen.")}</p>
          <div className="space-y-1">
            <button data-testid="kiosk-widget-wheel" onClick={() => toggleKioskWidget("kiosk_show_wheel", kioskWheel, setKioskWheel)} disabled={!dataReady}
              className="w-full flex items-center justify-between py-2 border-b-2 border-dashed border-brand-ink/10 disabled:opacity-50">
              <span className="font-bold text-sm">🎡 {t("Rad van Fortuin")}</span>
              <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${kioskWheel ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
              </span>
            </button>
            <button data-testid="kiosk-widget-phone" onClick={() => toggleKioskWidget("kiosk_show_phone_tile", kioskPhoneTile, setKioskPhoneTile)} disabled={!dataReady}
              className="w-full flex items-center justify-between py-2 border-b-2 border-dashed border-brand-ink/10 disabled:opacity-50">
              <span className="font-bold text-sm">📱 {t("'Op je telefoon'-tegel")}</span>
              <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${kioskPhoneTile ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
              </span>
            </button>
            <button data-testid="kiosk-widget-nofs" onClick={async () => { const nv = !kioskNoFs; setKioskNoFs(nv); try { await api.put("/settings", { kiosk_no_fullscreen: nv }); await refreshSettings?.(); toast.success(t("Kiosk-instelling bijgewerkt")); } catch { toast.error(t("Mislukt")); } }} disabled={!dataReady}
              className="w-full flex items-center justify-between py-2 border-b-2 border-dashed border-brand-ink/10 disabled:opacity-50">
              <span className="font-bold text-sm flex items-center gap-1.5">🖥️ {t("Volledig scherm bij vergrendelen")}
                <InfoTip testid="info-kiosk-nofs" text={t("Aan: de kiosk gaat bij vergrendelen naar volledig scherm. In een gewoon browsertabblad toont de browser dan kort 'sleep vanaf boven om te sluiten'. Uit: geen volledig scherm en dus geen browsermelding — maar de adresbalk blijft dan zichtbaar. Beste oplossing: zet de kiosk als app op het beginscherm; dan is er sowieso geen melding.")} /></span>
              <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${!kioskNoFs ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
              </span>
            </button>
            <button data-testid="kiosk-widget-allowance" onClick={() => toggleKioskWidget("kiosk_show_allowance", kioskAllowance, setKioskAllowance)} disabled={!dataReady}
              className="w-full flex items-center justify-between py-2 last:border-0 disabled:opacity-50">
              <span className="font-bold text-sm">💰 {t("Zakgeld-widget (wie heeft nog punten nodig)")}</span>
              <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${kioskAllowance ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
              </span>
            </button>
            <div className="border-t border-brand-ink/10 mt-1 pt-2">
              <p className="font-bold text-sm mb-1">🗓️ {t("Standaard kalenderweergave")}</p>
              <div className="flex gap-2">
                {["week", "month"].map((v) => (
                  <button key={v} data-testid={`cal-view-${v}`} disabled={!dataReady}
                    onClick={async () => { setCalView(v); await api.put("/settings", { default_calendar_view: v }); await refreshSettings?.(); toast.success(t("Opgeslagen")); }}
                    className={`flex-1 brutal-sm brutal-press rounded-full px-3 py-2 text-sm font-bold disabled:opacity-50 ${calView === v ? "bg-brand-violet text-white" : "bg-white"}`}>{v === "week" ? t("Week") : t("Maand")}</button>
                ))}
              </div>
            </div>
            <div className="border-t border-brand-ink/10 mt-1 pt-2">
              <button data-testid="kiosk-widget-delete" onClick={() => toggleKioskWidget("kiosk_delete_enabled", kioskDelete, setKioskDelete)} disabled={!dataReady}
                className="w-full flex items-center justify-between py-2 disabled:opacity-50">
                <span className="font-bold text-sm">🗑️ {t("Taken verwijderen via kiosk toestaan")}</span>
                <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${kioskDelete ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                  <span className="w-5 h-5 rounded-full bg-white brutal-sm" />
                </span>
              </button>
              {kioskDelete && (
                <div className="flex gap-2 mt-1">
                  {[{ v: "admins", l: t("Alleen beheerders") }, { v: "everyone", l: t("Iedereen") }].map((o) => (
                    <button key={o.v} data-testid={`kiosk-delete-who-${o.v}`} onClick={async () => { setKioskDeleteWho(o.v); await api.put("/settings", { kiosk_delete_who: o.v }); toast.success(t("Kiosk-instelling bijgewerkt")); }}
                      className={`flex-1 brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold ${kioskDeleteWho === o.v ? "bg-brand-violet text-white" : "bg-white"}`}>{o.l}</button>
                  ))}
                </div>
              )}
              <p className="text-xs text-brand-ink/50 mt-1.5">{t("Verwijderen gebeurt altijd via pincode en wordt gelogd. Staat dit uit, dan kan er niet via de kiosk verwijderd worden.")}</p>
            </div>
          </div>
        </BrutalCard>

        <BrutalCard data-testid="kiosk-layout-card">
          <p className="font-head font-bold flex items-center gap-2"><GripVertical className="w-4 h-4" strokeWidth={2.5} />{t("Kiosk — indeling (slepen)")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("Sleep blokken om de volgorde te bepalen of ze tussen de kolommen te verplaatsen. Tik op het oog om een blok te verbergen op de kiosk.")}</p>
          {dataReady && <KioskLayoutEditor key={`${effOrg}-kl`} initial={kioskLayoutRaw} onSave={saveLayout} t={t} />}
        </BrutalCard>

        <BrutalCard data-testid="kiosk-lock-card">
          <p className="font-head font-bold flex items-center gap-2"><KeyRound className="w-4 h-4" strokeWidth={2.5} />{t("Kiosk-vergrendel-PIN")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("Met deze PIN kan de vergrendelde kioskmodus (fullscreen) worden verlaten. Beheerders kunnen ook met hun eigen persoonlijke PIN ontgrendelen.")}</p>
          <div className="flex items-center gap-2">
            <input data-testid="kiosk-lock-pin-input" type="password" inputMode="numeric" maxLength={6}
              value={lockPinInput} onChange={(e) => setLockPinInput(e.target.value.replace(/\D/g, ""))}
              placeholder={lockPinSet ? t("Nieuwe PIN (4–6 cijfers)") : t("PIN (4–6 cijfers)")}
              className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2.5 font-head font-black tracking-[0.3em] text-center outline-none" />
            <BrutalButton data-testid="kiosk-lock-pin-save" size="sm" className="shrink-0" onClick={() => saveLockPin(false)} disabled={lockPinBusy}>{t("Opslaan")}</BrutalButton>
          </div>
          {lockPinSet && (
            <div className="flex items-center justify-between mt-2">
              <Pill className="bg-brand-green">{t("PIN ingesteld ✓")}</Pill>
              <button data-testid="kiosk-lock-pin-clear" onClick={() => saveLockPin(true)} disabled={lockPinBusy}
                className="text-xs font-bold text-brand-pink underline">{t("Verwijderen")}</button>
            </div>
          )}
        </BrutalCard>

        <BrutalCard data-testid="kiosk-language-card">
          <p className="font-head font-bold flex items-center gap-2"><Languages className="w-4 h-4" strokeWidth={2.5} />{t("Kiosk-taal")}</p>
          <p className="text-xs text-brand-ink/60 mt-0.5 mb-3">{t("In welke taal toont het kioskbord de mededelingen? Berichten worden automatisch vertaald naar deze taal.")}</p>
          <div className="grid grid-cols-2 gap-2" data-testid="kiosk-language-options">
            {[["nl", "🇳🇱 Nederlands"], ["en", "🇬🇧 English"], ["tr", "🇹🇷 Türkçe"], ["ar", "🇲🇦 العربية"]].map(([code, label]) => (
              <button key={code} data-testid={`kiosk-lang-${code}`}
                onClick={async () => { setKioskLang(code); try { await api.put("/settings", { kiosk_language: code }); await refreshSettings(); toast.success(t("Kiosk-taal opgeslagen")); } catch (e) { toast.error(t("Mislukt")); } }}
                className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold ${kioskLang === code ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>{label}</button>
            ))}
          </div>
        </BrutalCard>
      </Section>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Nodig uit voor")} {orgName}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <img src={joinOrgQr} alt="QR" className="w-52 h-52 brutal-sm rounded-xl bg-white p-1" data-testid="join-qr-img" />
            {effOrg && (
              <div className="w-full flex items-center gap-2 bg-brand-cream brutal-sm rounded-lg px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-brand-ink/50 font-bold">{t("Organisatie-ID")}</p>
                  <p className="font-head font-black tracking-widest truncate" data-testid="qr-org-id">{effOrg}</p>
                </div>
                <button data-testid="qr-copy-orgid" onClick={() => { navigator.clipboard?.writeText(effOrg); toast.success(t("Organisatie-ID gekopieerd")); }} className="brutal-sm rounded-lg p-2 bg-brand-yellow"><Copy className="w-4 h-4" strokeWidth={2.5} /></button>
              </div>
            )}
            <div className="flex gap-2 w-full flex-wrap">
              <BrutalButton data-testid="qr-copy-link" variant="green" size="sm" className="flex-1" onClick={() => { navigator.clipboard?.writeText(joinOrgLink); toast.success(t("Link gekopieerd")); }}><Copy className="w-4 h-4" strokeWidth={2.5} />{t("Link")}</BrutalButton>
              <BrutalButton data-testid="qr-wa" size="sm" className="flex-1" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Sluit je aan bij ${orgName} op Slackr\nMeld je hier aan (organisatie is al gekoppeld):\n${joinOrgLink}\n\nOf voer bij 'Aansluiten' dit organisatie-ID in: ${effOrg}`)}`, "_blank")}><MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</BrutalButton>
              <BrutalButton data-testid="qr-email" variant="pink" size="sm" className="flex-1" onClick={() => window.open(`mailto:?subject=${encodeURIComponent(`Uitnodiging voor ${orgName}`)}&body=${encodeURIComponent(`Hoi!\n\nSluit je aan bij ${orgName} op Slackr. Meld je hier aan (organisatie is al gekoppeld):\n${joinOrgLink}\n\nOf voer bij 'Aansluiten' dit organisatie-ID in: ${effOrg}\n\nTot snel!`)}`)}><Mail className="w-4 h-4" strokeWidth={2.5} />E-mail</BrutalButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><UserCog className="w-5 h-5" strokeWidth={2.5} />{t("Nieuwe gebruiker")}</DialogTitle></DialogHeader>
          <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Naam")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("E-mail (optioneel)")} className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
          <input data-testid="user-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder={t("Pincode (optioneel)")} inputMode="numeric" className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("Telefoon")} inputMode="tel" className="brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
            <DatePicker testid="user-birthdate-input" value={form.birthdate} onChange={(v) => setForm({ ...form, birthdate: v })} fromYear={1920} toYear={new Date().getFullYear()} placeholder={t("Geboortedatum")} className="px-3 py-2.5" />
          </div>
          <div className="flex gap-2">
            {["gebruiker", "beheerder", "super_admin"].map((r) => (
              <button key={r} onClick={() => setForm({ ...form, role: r })} className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold ${form.role === r ? "bg-brand-blue text-white" : "bg-white"}`}>{t(ROLE_LABELS[r])}</button>
            ))}
          </div>
          <BrutalButton data-testid="create-user-btn" onClick={create} className="w-full">{t("Aanmaken")}</BrutalButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
